import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { convertToCSV, downloadFile, formatDateForFilename } from '@/lib/export-utils';
import { toast } from 'sonner';

export interface CompanyRow {
  id: string;
  company_name: string;
  domain: string | null;
  website: string | null;
  primary_category: string | null;
  ai_subcategory: string | null;
  headquarters_country: string | null;
  headquarters_state: string | null;
  headquarters_city: string | null;
  region: string | null;
  employee_range: string | null;
  employee_count: number | null;
  icp_tier: string | null;
  icp_score: number | null;
  sales_rep: string | null;
  territory: string | null;
  modalities: string[] | null;
  therapeutic_areas: string[] | null;
  clinical_phase: string | null;
  development_stage: string | null;
  funding_status: string | null;
  carr_modality: string | null;
  carr_cell_types: string[] | null;
  carr_pipeline_count: number | null;
  carr_highest_phase: string | null;
  carr_funding_round: string | null;
  carr_total_raised_usd: number | null;
  carr_ownership_type: string | null;
  carr_drug_names: string | null;
  carr_delivery_system: string | null;
  account_tier: string | null;
  life_science_id: string | null;
  parent_company_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  contacts?: { count: number }[];
}

export interface AccountFilters {
  searchTerm?: string;
  territories?: string[];     // combined rep/territory filter (standardized territory column)
  countries?: string[];
  carrModalities?: string[];  // product sourcing: Allogeneic/Autologous/Both
  modalities?: string[];      // canonical modality (CAR-T, Gene Therapy, etc.)
  therapeuticAreas?: string[]; // canonical therapeutic area (Oncology, Neurology, etc.)
  clinicalPhases?: string[];
  fundingStatuses?: string[];
  employeeRanges?: string[];
  topAccount?: boolean;
  accountTiers?: string[];
  icpTiers?: string[];
  primaryCategories?: string[];  // therapeutic_developer, service_provider, etc.
  subcategories?: string[];      // ai_subcategory values
  hasContacts?: boolean;         // backend-only, not in UI
  hideInactive?: boolean;        // hide disqualified accounts
  limit?: number;
  offset?: number;
}

export function useAccounts(filters: AccountFilters) {
  return useQuery({
    queryKey: ['accounts', filters],
    queryFn: async () => {
      let query = (supabase.from('companies') as any)
        .select(
          'id, company_name, domain, website, primary_category, ai_subcategory, headquarters_country, headquarters_state, headquarters_city, region, employee_range, employee_count, icp_tier, icp_score, sales_rep, territory, modalities, therapeutic_areas, clinical_phase, development_stage, funding_status, account_tier, carr_modality, carr_cell_types, carr_pipeline_count, carr_highest_phase, carr_funding_round, carr_total_raised_usd, carr_ownership_type, carr_drug_names, carr_delivery_system, life_science_id, parent_company_name, created_at, updated_at, contacts(count)',
          { count: 'exact' }
        )
        .eq('is_icp_relevant', true)
        .neq('primary_category', 'academic_institution');

      if (filters.searchTerm?.trim()) {
        query = query.or(
          `company_name.ilike.%${filters.searchTerm.trim()}%,domain.ilike.%${filters.searchTerm.trim()}%`
        );
      }

      if (filters.territories?.length) {
        const hasUnassigned = filters.territories.includes('__unassigned__');
        const namedTerritories = filters.territories.filter((t) => t !== '__unassigned__');
        if (hasUnassigned && namedTerritories.length > 0) {
          query = query.or(`territory.in.(${namedTerritories.join(',')}),territory.is.null`);
        } else if (hasUnassigned) {
          query = query.is('territory', null);
        } else {
          query = query.in('territory', namedTerritories);
        }
      }

      if (filters.countries?.length) {
        query = query.in('headquarters_country', filters.countries);
      }

      if (filters.carrModalities?.length) {
        query = query.in('carr_modality', filters.carrModalities);
      }

      if (filters.modalities?.length) {
        // "Cell Type" in the UI filters canonical modality values in companies.modalities[].
        // companies.carr_cell_types[] is a separate, CARR-specific column not exposed as a filter yet.
        const { data: matchingIds, error: modErr } = await (supabase.rpc as any)(
          'get_companies_by_canonical_modality',
          { canonical_values: filters.modalities }
        );
        if (modErr) throw modErr;
        if (matchingIds?.length) {
          query = query.in('id', matchingIds.map((r: any) => r.id));
        } else {
          return { data: [] as CompanyRow[], totalCount: 0 };
        }
      }

      if (filters.therapeuticAreas?.length) {
        const { data: matchingIds, error: taErr } = await (supabase.rpc as any)(
          'get_companies_by_canonical_therapeutic_area',
          { canonical_values: filters.therapeuticAreas }
        );
        if (taErr) throw taErr;
        if (matchingIds?.length) {
          query = query.in('id', matchingIds.map((r: any) => r.id));
        } else {
          return { data: [] as CompanyRow[], totalCount: 0 };
        }
      }

      if (filters.primaryCategories?.length) {
        query = query.in('primary_category', filters.primaryCategories);
      }

      if (filters.subcategories?.length) {
        query = query.in('ai_subcategory', filters.subcategories);
      }

      if (filters.clinicalPhases?.length) {
        query = query.in('clinical_phase', filters.clinicalPhases);
      }

      if (filters.fundingStatuses?.length) {
        query = query.in('funding_status', filters.fundingStatuses);
      }

      if (filters.employeeRanges?.length) {
        query = query.in('employee_range', filters.employeeRanges);
      }

      if (filters.topAccount) {
        query = query.eq('top_account', true);
      }

      if (filters.accountTiers?.length) {
        query = query.in('account_tier', filters.accountTiers);
      }

      if (filters.icpTiers?.length) {
        query = query.in('icp_tier', filters.icpTiers);
      }

      if (filters.hasContacts === false) {
        const { data: zeroIds, error: zeroErr } = await (supabase as any)
          .from('v_zero_contact_accounts').select('id');
        if (zeroErr) throw zeroErr;
        if (zeroIds?.length) {
          query = query.in('id', zeroIds.map((r: any) => r.id));
        } else {
          return { data: [] as CompanyRow[], totalCount: 0 };
        }
      } else if (filters.hasContacts === true) {
        const { data: zeroIds, error: zeroErr } = await (supabase as any)
          .from('v_zero_contact_accounts').select('id');
        if (zeroErr) throw zeroErr;
        if (zeroIds?.length) {
          query = query.not('id', 'in', `(${zeroIds.map((r: any) => r.id).join(',')})`);
        }
      }

      if (filters.hideInactive) {
        query = query.neq('account_tier', 'disqualified');
      }

      query = query
        .order('company_name', { ascending: true })
        .range(filters.offset || 0, (filters.offset || 0) + (filters.limit || 25) - 1);

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        data: (data || []) as unknown as CompanyRow[],
        totalCount: count || 0,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAccountFilterOptions() {
  return useQuery({
    queryKey: ['account-filter-options'],
    queryFn: async () => {
      const [countries, carrModalities, modalities, empRanges, territories, therapeuticAreas, fundingStatuses, clinicalPhases, accountTiers, icpTiers, primaryCategories, subcategories] =
        await Promise.all([
          supabase.from('v_filter_countries').select('headquarters_country'),
          (supabase.from('v_filter_carr_modalities') as any).select('carr_modality, sort_order').order('sort_order'),
          (supabase.from('v_filter_modalities') as any).select('modality, account_count, sort_order').order('sort_order'),
          supabase.from('v_filter_employee_ranges').select('employee_range'),
          supabase.from('v_filter_territories').select('territory'),
          (supabase.from('v_filter_therapeutic_areas') as any).select('therapeutic_area, account_count, sort_order').order('sort_order'),
          (supabase.from('v_filter_funding_status') as any).select('funding_status'),
          (supabase.from('companies') as any).select('clinical_phase').eq('is_icp_relevant', true).not('clinical_phase', 'is', null).limit(500),
          (supabase.from('companies') as any).select('account_tier').eq('is_icp_relevant', true).not('account_tier', 'is', null).limit(500),
          (supabase.from('companies') as any).select('icp_tier').eq('is_icp_relevant', true).not('icp_tier', 'is', null).limit(500),
          supabase.from('v_filter_primary_categories').select('primary_category'),
          (supabase.from('v_filter_ai_subcategories') as any).select('val'),
        ]);

      const unique = (arr: any[] | null, key: string) =>
        [...new Set((arr || []).map((r: any) => r[key]).filter(Boolean))].sort() as string[];

      return {
        countries: unique(countries.data, 'headquarters_country'),
        carrModalities: (carrModalities.data || []).map((r: any) => r.carr_modality).filter(Boolean) as string[],
        modalities: (modalities.data || []).map((r: any) => ({ value: r.modality, count: r.account_count })).filter((r: any) => r.value) as { value: string; count: number }[],
        employeeRanges: unique(empRanges.data, 'employee_range'),
        territories: unique(territories.data, 'territory'),
        therapeuticAreas: (therapeuticAreas.data || []).map((r: any) => ({ value: r.therapeutic_area, count: r.account_count })).filter((r: any) => r.value) as { value: string; count: number }[],
        fundingStatuses: unique(fundingStatuses.data, 'funding_status'),
        clinicalPhases: unique(clinicalPhases.data, 'clinical_phase'),
        accountTiers: unique(accountTiers.data, 'account_tier'),
        icpTiers: unique(icpTiers.data, 'icp_tier'),
        primaryCategories: unique(primaryCategories.data, 'primary_category'),
        subcategories: unique(subcategories.data, 'val'),
      };
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useExportAccounts() {
  return useMutation({
    mutationFn: async (filters: AccountFilters) => {
      let query = (supabase.from('companies') as any)
        .select(
          'id, company_name, domain, primary_category, ai_subcategory, headquarters_country, headquarters_state, headquarters_city, region, employee_range, employee_count, icp_tier, icp_score, sales_rep, territory, modalities, therapeutic_areas, clinical_phase, development_stage, funding_status, account_tier, carr_modality, life_science_id, created_at, updated_at'
        )
        .eq('is_icp_relevant', true)
        .neq('primary_category', 'academic_institution');

      if (filters.searchTerm?.trim()) {
        query = query.or(
          `company_name.ilike.%${filters.searchTerm.trim()}%,domain.ilike.%${filters.searchTerm.trim()}%`
        );
      }
      if (filters.territories?.length) {
        const hasUnassigned = filters.territories.includes('__unassigned__');
        const namedTerritories = filters.territories.filter((t) => t !== '__unassigned__');
        if (hasUnassigned && namedTerritories.length > 0) {
          query = query.or(`territory.in.(${namedTerritories.join(',')}),territory.is.null`);
        } else if (hasUnassigned) {
          query = query.is('territory', null);
        } else {
          query = query.in('territory', namedTerritories);
        }
      }
      if (filters.countries?.length) query = query.in('headquarters_country', filters.countries);
      if (filters.carrModalities?.length) query = query.in('carr_modality', filters.carrModalities);
      if (filters.primaryCategories?.length) query = query.in('primary_category', filters.primaryCategories);
      if (filters.subcategories?.length) query = query.in('ai_subcategory', filters.subcategories);
      if (filters.clinicalPhases?.length) query = query.in('clinical_phase', filters.clinicalPhases);
      if (filters.fundingStatuses?.length) query = query.in('funding_status', filters.fundingStatuses);
      if (filters.employeeRanges?.length) query = query.in('employee_range', filters.employeeRanges);
      if (filters.topAccount) query = query.eq('top_account', true);
      if (filters.accountTiers?.length) query = query.in('account_tier', filters.accountTiers);
      if (filters.icpTiers?.length) query = query.in('icp_tier', filters.icpTiers);
      if (filters.hideInactive) {
        query = query.neq('account_tier', 'disqualified');
      }

      query = query.order('company_name', { ascending: true }).limit(10000);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as CompanyRow[];
    },
  });
}
